/**
 * Phase 24.4a — Toast Zustand store + useToast hook.
 *
 * Single-toast dispatcher (replace-on-conflict semantic per S4 Round 3
 * Q4): when a new toast fires while another is showing, the latter
 * replaces the former. Simpler than queue management; matches user
 * expectation that rapid-fire actions shouldn't backlog stale toasts.
 *
 * Components import `useToast()` (hook), not the underlying store
 * directly. Hides Zustand implementation detail per S4 closing reminder
 * + matches existing useAuthStore precedent.
 */
import { create } from "zustand";

export type ToastVariant = "success" | "error" | "info";

export interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
  /** Auto-dismiss timeout in ms. Null disables auto-dismiss. Default 3000. */
  durationMs?: number | null;
}

interface ToastState {
  currentToast: Toast | null;
  show: (input: { message: string; variant?: ToastVariant; durationMs?: number | null }) => void;
  dismiss: () => void;
}

let nextId = 1;

/**
 * Underlying Zustand store. Components MUST go through `useToast()` —
 * exported here for test reset (`useToastStore.setState({...})`) and
 * future imperative dispatch from non-React contexts.
 */
export const useToastStore = create<ToastState>((set) => ({
  currentToast: null,
  show: ({ message, variant = "info", durationMs = 3000 }) => {
    const toast: Toast = { id: nextId++, message, variant, durationMs };
    set({ currentToast: toast });
  },
  dismiss: () => set({ currentToast: null }),
}));

/**
 * Public hook. Components call this — never useToastStore directly.
 * Mirrors the useAuthStore wrap pattern for clean store-implementation
 * encapsulation.
 */
export function useToast() {
  const currentToast = useToastStore((s) => s.currentToast);
  const show = useToastStore((s) => s.show);
  const dismiss = useToastStore((s) => s.dismiss);
  return { currentToast, show, dismiss };
}
